import React, { useState, useEffect } from "react";
import { Modal } from "../common/ui/modal";
import Button from "../common/ui/button/Button";
import { Bell, Users, User } from "lucide-react";
import { ReminderTiming, CreateReminderDto } from "@secure-caseworker/shared";

type ReminderOption = {
  id: string;
  label: string;
  timing: ReminderTiming;
  offset_days: number;
};

const REMINDER_OPTIONS: ReminderOption[] = [
  {
    id: "before_3",
    label: "3 days before due date",
    timing: ReminderTiming.BEFORE_DUE,
    offset_days: 3,
  },
  {
    id: "on_due",
    label: "On due date",
    timing: ReminderTiming.ON_DUE,
    offset_days: 0,
  },
  {
    id: "after_3",
    label: "3 days after due date",
    timing: ReminderTiming.AFTER_DUE,
    offset_days: 3,
  },
  {
    id: "after_7",
    label: "7 days after due date",
    timing: ReminderTiming.AFTER_DUE,
    offset_days: 7,
  },
];

// Helper to convert reminder DTOs to selected IDs
const remindersToSelectedIds = (
  reminders: CreateReminderDto[]
): Set<string> => {
  const ids = new Set<string>();
  for (const reminder of reminders) {
    const option = REMINDER_OPTIONS.find(
      (o) =>
        o.timing === reminder.timing && o.offset_days === reminder.offset_days
    );
    if (option) {
      ids.add(option.id);
    }
  }
  return ids;
};

// Helper to convert selected IDs to reminder DTOs
const selectedIdsToReminders = (ids: Set<string>): CreateReminderDto[] => {
  const reminders: CreateReminderDto[] = [];
  for (const id of ids) {
    const option = REMINDER_OPTIONS.find((o) => o.id === id);
    if (option) {
      reminders.push({
        timing: option.timing,
        offset_days: option.offset_days,
      });
    }
  }
  return reminders;
};

export type EditRemindersParams = {
  reminders: CreateReminderDto[];
};

type EditRemindersModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (params: EditRemindersParams) => Promise<void>;
  mode: "single" | "all";
  participantName?: string;
  participantCount?: number;
  currentReminders?: CreateReminderDto[];
  hasDueDate: boolean;
};

export const EditRemindersModal: React.FC<EditRemindersModalProps> = ({
  isOpen,
  onClose,
  onSave,
  mode,
  participantName,
  participantCount,
  currentReminders = [],
  hasDueDate,
}) => {
  const [selectedReminders, setSelectedReminders] = useState<Set<string>>(
    new Set(["before_3"])
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize selected reminders when modal opens
  useEffect(() => {
    if (isOpen) {
      if (currentReminders && currentReminders.length > 0) {
        setSelectedReminders(remindersToSelectedIds(currentReminders));
      } else {
        // Default to 3 days before
        setSelectedReminders(new Set(["before_3"]));
      }
    }
    // Only run when isOpen changes to true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const reminders = selectedIdsToReminders(selectedReminders);
      await onSave({ reminders });
      handleClose();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update reminders";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSelectedReminders(new Set(["before_3"]));
      setError(null);
    }, 200);
  };

  const toggleReminder = (reminderId: string) => {
    setSelectedReminders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reminderId)) {
        newSet.delete(reminderId);
      } else {
        newSet.add(reminderId);
      }
      return newSet;
    });
  };

  const title =
    mode === "all"
      ? "Edit Reminders for All Participants"
      : `Edit Reminders${participantName ? ` for ${participantName}` : ""}`;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} maxWidth="480px">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Mode indicator */}
        <div className="flex items-start gap-3 rounded-lg bg-gray-50 dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700">
          {mode === "all" ? (
            <>
              <Users className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This will update the reminder schedule for{" "}
                <strong>{participantCount}</strong> participant
                {participantCount !== 1 ? "s" : ""} with pending status.
              </p>
            </>
          ) : (
            <>
              <User className="h-5 w-5 text-gray-500 dark:text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Update the reminder schedule for this participant.
              </p>
            </>
          )}
        </div>

        {/* Reminder Schedule */}
        {hasDueDate ? (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              <Bell className="inline h-4 w-4 mr-1.5 -mt-0.5" />
              Reminder Schedule
            </label>
            <div className="space-y-2">
              {REMINDER_OPTIONS.map((option) => (
                <label
                  key={option.id}
                  className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 p-2 rounded-lg -mx-2"
                >
                  <input
                    type="checkbox"
                    checked={selectedReminders.has(option.id)}
                    onChange={() => toggleReminder(option.id)}
                    className="mr-3 h-4 w-4 text-brand-600 focus:ring-brand-500 rounded border-gray-300 dark:border-gray-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
            {selectedReminders.size === 0 && (
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                No reminders will be sent{" "}
                {mode === "all"
                  ? "to these participants"
                  : "for this participant"}
              </p>
            )}
          </div>
        ) : (
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 border border-amber-200 dark:border-amber-800">
            <Bell className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Set a due date on the request to enable reminder scheduling.
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-800 dark:text-red-200">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={loading || !hasDueDate}
          >
            {loading ? "Saving..." : "Save Reminders"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
